import { clsx } from 'clsx';
import { useMemo } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useMeasure } from 'context/measure';
import { abbrToUppercase } from 'helpers/dimension';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { Assessment } from 'types/api/envScales/Assessment';
import { PlusCircleFilled } from '@teachstonellc/component-library/dist/icons';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@radix-ui/react-accordion';

import {
  Section,
  SectionHeader,
  SectionContent
} from 'components/Measure/View/Styled';

export type CycleNote = {
  cycleNumber: number;
  notes: string;
};

export type DimensionNote = {
  id: number;
  name: string;
  cycles: CycleNote[];
};

/**
 * Builds one entry per dimension in `ageLevel`, with the notes recorded for
 * it across the assessment's cycles. Pass a focus-filtered `ageLevel` (see
 * `helpers/dimension/focusDimensions`) to scope this to an informal
 * observation's in-focus dimensions.
 */
export function buildDimensionNotes(
  ageLevel: AgeLevel,
  assessment: Assessment
): DimensionNote[] {
  const cycles = assessment.observation?.observation_cycles ?? [];

  const notesByDimensionId = new Map<number, CycleNote[]>();

  for (const cycle of cycles) {
    for (const score of cycle.dimension_scores ?? []) {
      const notes = score.dimension_notes ?? '';
      if (notes.replace(/•/g, '').trim().length === 0) continue;

      const existing = notesByDimensionId.get(score.dimension_id) ?? [];
      existing.push({
        cycleNumber: cycle.cycle_number,
        notes
      });
      notesByDimensionId.set(score.dimension_id, existing);
    }
  }

  return ageLevel.domains.flatMap(domain =>
    domain.dimensions.map(dimension => ({
      id: dimension.id,
      name: `${dimension.name} (${abbrToUppercase(dimension.abbreviation)})`,
      cycles: notesByDimensionId.get(dimension.id) ?? []
    }))
  );
}

interface ObservationNotesProps {
  assessment: Assessment;
}

export function ObservationNotes({ assessment }: ObservationNotesProps) {
  const { ageLevel } = useMeasure();

  const dimensions = useMemo(
    () => buildDimensionNotes(ageLevel, assessment),
    [ageLevel, assessment]
  );

  return <ObservationNotesView dimensionNotes={dimensions} />;
}

interface DimensionNotesAccordionProps {
  dimensionNotes: DimensionNote[];
}

/** Just the notes accordion, so an embedding page can supply its own heading. */
export function DimensionNotesAccordion({
  dimensionNotes
}: DimensionNotesAccordionProps) {
  return (
    <Accordion type="multiple" className="flex flex-col gap-3">
      {dimensionNotes.map(dim => {
        const hasNotes = dim.cycles.length > 0;
        const cycleLabel = i18n.ft(messages.cycles, {
          count: dim.cycles.length
        });

        return (
          <AccordionItem
            key={dim.id}
            value={String(dim.id)}
            disabled={!hasNotes}
            className="shadow-scale-sm rounded-xl bg-white"
          >
            <AccordionTrigger
              className={clsx(
                'w-full p-3 flex items-center gap-3 group',
                !hasNotes && 'cursor-default'
              )}
            >
              <PlusCircleFilled
                size={24}
                className={clsx(
                  'shrink-0 transition-transform duration-200',
                  hasNotes
                    ? 'text-scale-blue group-data-[state=open]:rotate-45'
                    : 'text-scale-blue opacity-40'
                )}
              />
              <div className="text-scale-dark-blue text-sm font-bold text-left">
                {dim.name}
              </div>
              <div className="w-px h-4 bg-scale-neutral-200 shrink-0" />
              <div className="text-scale-neutral-500 text-sm">{cycleLabel}</div>
            </AccordionTrigger>

            <AccordionContent>
              <div className="pb-3 px-3 pt-1 flex gap-3">
                {/* -mt-6 bridges the trigger's bottom padding so the line touches the icon */}
                <div className="w-6 shrink-0 flex justify-center -mt-6">
                  <div className="w-1 bg-scale-blue" />
                </div>

                <div className="flex-1 flex flex-col gap-2">
                  {dim.cycles.map(cycle => (
                    <div
                      key={cycle.cycleNumber}
                      className="border border-scale-neutral-200 rounded-lg p-3"
                    >
                      <div className="py-0.5 px-1.5 rounded border border-scale-neutral-200 bg-scale-neutral-50 text-scale-neutral-500 text-xs inline-block mb-3">
                        {i18n.ft(messages.cycleNumber, {
                          number: cycle.cycleNumber
                        })}
                      </div>

                      <div className="pl-2 text-sm whitespace-pre-wrap">
                        {cycle.notes}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

interface ObservationNotesViewProps {
  dimensionNotes: DimensionNote[];
}

export function ObservationNotesView({
  dimensionNotes
}: ObservationNotesViewProps) {
  return (
    <Section className="mt-2">
      <SectionHeader>{i18n.ft(messages.title)}</SectionHeader>

      <SectionContent>
        <DimensionNotesAccordion dimensionNotes={dimensionNotes} />
      </SectionContent>
    </Section>
  );
}
